import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { listSkills } from "@/lib/api";
import { DOMAINS } from "@/lib/domains";
import type { Skill } from "@/lib/types";

// Landing card matching the design mockup: bold title + green verified check, a mono category · #tags
// line, a two-line summary, a hairline divider, then the SCORE chip + star rating and installs.
// No bookmark / author / "days ago" — this is the marketing card, not the Explore card.
function CarouselCard({ skill }: { skill: Skill }) {
  const score = skill.security_score ?? 90;
  const rating = skill.rating?.avg ?? 4.5;
  const downloads = skill.metrics?.downloads ?? 0;
  const tags = (skill.tags ?? []).slice(0, 2);

  return (
    <article
      className="relative flex h-full w-full flex-col rounded-[12px] border p-5 transition-transform duration-200"
      style={{ background: "var(--bg-elev)", borderColor: "var(--border)", borderTopColor: "var(--border-light)" }}
    >
      <span aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-[1.5px]" style={{ background: "linear-gradient(90deg, transparent, rgba(225,100,46,0.35), transparent)" }} />

      {/* Title + verified check */}
      <div className="flex items-center gap-2">
        <h3 className="truncate font-display text-[16px] font-bold tracking-tight first-letter:uppercase" style={{ color: "var(--text)" }}>{skill.name}</h3>
        <span className="shrink-0 text-green-500">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
        </span>
      </div>

      {/* Category · tags */}
      <p className="mt-2.5 truncate font-mono text-[11px]" style={{ color: "var(--text-muted)" }}>
        {skill.domains?.[0] || "General"}
        {tags.length > 0 && <>&nbsp;&nbsp;·&nbsp;&nbsp;{tags.map((t) => `#${t}`).join(" ")}</>}
      </p>

      {/* Summary — fixed two lines so cards stay a uniform height */}
      <p className="mt-2.5 line-clamp-2 min-h-[2.7em] text-[13px] font-light leading-relaxed" style={{ color: "var(--text-secondary)" }}>
        {skill.summary || "No summary provided."}
      </p>

      {/* Divider */}
      <div className="mt-4 h-px w-full" style={{ background: "var(--border)" }} />

      {/* Score + rating */}
      <div className="mt-3.5 flex items-center gap-4">
        <span className="inline-flex items-center gap-1.5 rounded-md border border-primary/30 bg-primary/[0.07] px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-primary">
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
          Score {score}
        </span>
        <span className="flex items-center gap-1.5 font-mono text-[12px]" style={{ color: "var(--text-muted)" }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></svg>
          {rating.toFixed(1)}
        </span>
      </div>

      {/* Installs */}
      <span className="mt-3 flex items-center gap-1.5 font-mono text-[12px] text-primary">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
        {downloads >= 1000 ? `${(downloads / 1000).toFixed(1)}k` : downloads} installs
      </span>
    </article>
  );
}

export default function SkillCarousel() {
  const [skills, setSkills] = useState<Skill[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const autoRef = useRef<number | null>(null);
  const pausedRef = useRef(false);        // true while hovering / focused
  const posRef = useRef(0);               // float scroll accumulator (el.scrollLeft rounds, so drive from this)

  useEffect(() => {
    // Pull a broad set, then feature up to 2 RANDOM skills per domain so the carousel spans the
    // catalog and varies on every load (new skills get a chance to surface). Each skill appears once.
    listSkills({ limit: 200 }).then((res) => {
      if (!res) return;
      const shuffle = <T,>(a: T[]) => {
        const arr = [...a];
        for (let i = arr.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [arr[i], arr[j]] = [arr[j], arr[i]];
        }
        return arr;
      };
      const picked: Skill[] = [];
      const used = new Set<string>();
      for (const d of DOMAINS) {
        const inDomain = shuffle(res.filter((s) => !used.has(s.id) && (s.domains?.includes(d.slug) ?? false)));
        for (const s of inDomain.slice(0, 2)) { picked.push(s); used.add(s.id); }
      }
      setSkills(shuffle(picked)); // shuffle the final order too, so domains aren't always in sequence
    }).catch(() => {});
  }, []);

  // Continuous auto-scroll to the right. Keeps drifting on hover; only pauses on keyboard focus (a11y)
  // and respects prefers-reduced-motion.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || skills.length === 0) return;
    if (typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;

    let last = 0;
    const SPEED = 72; // px per second
    const frame = (ts: number) => {
      if (last === 0) last = ts;
      const dt = (ts - last) / 1000;
      last = ts;
      if (el.scrollWidth > 0) {
        const half = el.scrollWidth / 2; // width of one set
        if (pausedRef.current) {
          // Respect the position the user landed on (hover / manual nudge).
          posRef.current = el.scrollLeft;
        } else {
          posRef.current += SPEED * dt;
          if (posRef.current >= half) posRef.current -= half; // seamless wrap
          el.scrollLeft = posRef.current; // el.scrollLeft rounds; posRef keeps the true float
        }
      }
      autoRef.current = requestAnimationFrame(frame);
    };
    autoRef.current = requestAnimationFrame(frame);
    return () => { if (autoRef.current) cancelAnimationFrame(autoRef.current); };
  }, [skills]);

  const pause = useCallback(() => { pausedRef.current = true; }, []);
  const resume = useCallback(() => { pausedRef.current = false; }, []);

  if (skills.length === 0) return null;

  return (
    <section className="lp-carousel-section">
      <div className="lp-carousel-inner">
        <div className="lp-carousel-head">
          <div className="lp-carousel-eyebrow"><span className="lp-carousel-eyebrow-line" />Explore skills</div>
          {/* Explore CTA lives in the header so it stays put and clickable while the rail scrolls. */}
          <Link to="/skills" className="lp-carousel-explore">
            Explore all
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
          </Link>
        </div>

        {/* Auto-scrolling marquee — keeps drifting on hover; pauses only on keyboard focus. The
            viewport fades cards out at both edges (mask) instead of a hard cut. */}
        <div
          className="lp-carousel-viewport"
          onFocusCapture={pause}
          onBlurCapture={resume}
        >
          <div ref={scrollRef} className="lp-carousel-track no-scrollbar">
            {[...skills, ...skills].map((skill, idx) => (
              <Link key={`${skill.id}-${idx}`} to={`/skill/${skill.slug}`} className="lp-carousel-cell" aria-hidden={idx >= skills.length} tabIndex={idx >= skills.length ? -1 : 0}>
                <CarouselCard skill={skill} />
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
